<template>
    <model-form
        :title="
            taskThingDateEstimate && taskThingDateEstimate.id
                ? `Edit Task Date Estimate`
                : 'Create Task Date Estimate'
        "
        :formFields="formFields"
        @save="save"
        @cancel="cancel"
    />
</template>
<script>
import { defineComponent } from "vue";

import ModelForm from "../common/models/ModelForm.vue";
import { buildSelectField, buildTextField } from "@/lib/vueModelUtils";
import {
    buildThingSchemaSelectField,
    getDateAttributeOptions,
    getThingSchemaForId,
    schemaContainsDateAttributes,
} from "@/lib/thingUtils";
import {
    buildExperimentAPI,
    buildTaskThingDateEstimatesAPIForExperiment,
} from "@/services/api.services";
import { withLoadingScreen } from "@/lib/loading";
import { buildDayByDateForExperimentStartDateField } from "@/lib/dateUtils";

export default defineComponent({
    props: {
        taskThingDateEstimate: {
            type: Object,
            required: false,
        },
        protocol: {
            type: Object,
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
    },

    components: {
        ModelForm,
    },

    data() {
        return {
            thingSchemaField: null,
            thingIdField: null,
            dateAttributeNameField: null,
            selectedThingSchema: null,
            dayField: null,
            dateField: null,
            formFields: [],
        };
    },

    mounted() {
        this.taskThingDateEstimatesAPI = buildTaskThingDateEstimatesAPIForExperiment(
            this.experiment.code
        );
        this.experimentAPI = buildExperimentAPI(this.protocol.code, this.experiment.code);
        this.buildFormFields();
    },

    methods: {
        buildFormFields() {
            this.selectedThingSchema = getThingSchemaForId(
                this.protocol,
                this.taskThingDateEstimate.thingSchemaId
            );
            this.thingSchemaField = buildThingSchemaSelectField({
                label: "Schema",
                abstractSchemaConfig: this.protocol,
                initialThingSchema: this.selectedThingSchema,
                changed: (schema) => {
                    this.selectedThingSchema = schema;
                    this.updateSelectedSchema();
                },
                schemaOptionFilterFn: (schema) => {
                    return schemaContainsDateAttributes(schema);
                },
            });

            this.dayField = buildTextField({
                label: "Estimated Day",
                value: this.taskThingDateEstimate.day,
            });

            this.dateField = buildDayByDateForExperimentStartDateField(
                this.taskThingDateEstimate.day,
                this.experiment.day0,
                (value) => {
                    this.dayField.value = value;
                }
            );

            this.dayField.changed = (value) => {
                const date =
                    value === ""
                        ? this.experiment.day0
                        : this.experiment.day0.plus({ days: value });
                this.dateField.value = date.toISODate();
            };

            this.thingIdField = buildSelectField({
                name: "Thing",
                options: [],
                value: this.taskThingDateEstimate.thingId,
            });

            this.dateAttributeNameField = buildSelectField({
                name: "Date Attribute",
                options: this.selectedThingSchema
                    ? getDateAttributeOptions(this.selectedThingSchema)
                    : [],
                value: this.taskThingDateEstimate.dateAttributeName,
            });

            this.formFields = [
                this.thingSchemaField,
                this.thingIdField,
                this.dateAttributeNameField,
                this.dayField,
                this.dateField,
            ];

            if (this.selectedThingSchema) {
                this.updateSelectedSchema();
            }
        },
        updateSelectedSchema() {
            this.updateThingOptions();
            this.updateAttributeOptions();
        },
        updateThingOptions() {
            withLoadingScreen(async () => {
                const things = await this.experimentAPI.fetchThingsForSchema(
                    this.selectedThingSchema.id
                );
                this.thingIdField.options = things.map((thing) => {
                    return {
                        title: thing.displayName,
                        value: thing.id,
                    };
                });
            });
        },
        updateAttributeOptions() {
            this.dateAttributeNameField.options = getDateAttributeOptions(this.selectedThingSchema);
            if (this.dateAttributeNameField.options.length === 1) {
                this.dateAttributeNameField.value = this.dateAttributeNameField.options[0].value;
            }
        },
        async save() {
            const taskThingDateEstimate = {
                ...this.taskThingDateEstimate,
                thingId: this.thingIdField.value,
                thingSchemaId: this.thingSchemaField.value,
                dateAttributeName: this.dateAttributeNameField.value,
                day: this.dayField.value,
            };

            if (this.taskThingDateEstimate.id) {
                await this.taskThingDateEstimatesAPI.update(taskThingDateEstimate);
            } else {
                await this.taskThingDateEstimatesAPI.create(taskThingDateEstimate);
            }
            this.$emit("saved");
        },
        cancel() {
            this.$emit("closed");
        },
        close() {
            this.$emit("closed");
        },
    },
});
</script>
